import { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { Button } from '../common/Button';
import { updatePostHogConsent } from '@/lib/posthog';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
}

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, required for the app
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already accepted cookies
    const consentGiven = localStorage.getItem('cookie_consent');
    if (!consentGiven) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (analyticsEnabled: boolean) => {
    const consentData = {
      essential: true,
      analytics: analyticsEnabled,
      timestamp: new Date().toISOString(),
    };

    // Save to localStorage
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));

    // Update PostHog consent state
    updatePostHogConsent(analyticsEnabled);

    // TODO: Save to database (user_preferences table) for logged-in users
    // This will be implemented after the user_preferences table is created

    setIsVisible(false);
  };

  const acceptAll = () => {
    savePreferences(true);
  };

  const acceptEssential = () => {
    savePreferences(false);
  };

  const acceptCustom = () => {
    savePreferences(preferences.analytics);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-surface-raised border-t-2 border-primary-500 shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-description"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <Cookie className="h-6 w-6 text-primary-600" aria-hidden="true" />
          </div>

          <div className="flex-1">
            <h2 className="text-lg font-semibold text-content-primary mb-2">Cookie Preferences</h2>
            <div id="cookie-consent-description">
              <p className="text-sm text-content-primary mb-3">
                We use cookies to ensure the basic functionality of our CRM platform and, with your consent, to analyze
                how you use the platform to improve your experience.
              </p>

              {!showDetails ? (
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium underline"
                >
                  Customize preferences
                </button>
              ) : (
                <div className="space-y-3 mb-4 p-4 bg-surface-raised rounded-lg border border-gray-200">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="cookie-essential"
                      checked={preferences.essential}
                      disabled
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 accent-primary-500 cursor-pointer disabled:opacity-50"
                      aria-describedby="cookie-essential-description"
                    />
                    <div className="flex-1">
                      <label htmlFor="cookie-essential" className="font-medium text-sm text-content-primary">
                        Essential Cookies <span className="text-content-secondary">(Required)</span>
                      </label>
                      <p id="cookie-essential-description" className="text-xs text-content-secondary mt-1">
                        These cookies are necessary for the platform to function and cannot be disabled. They include
                        session cookies, authentication tokens, and security features.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="cookie-analytics"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 accent-primary-500 cursor-pointer"
                      aria-describedby="cookie-analytics-description"
                    />
                    <div className="flex-1">
                      <label htmlFor="cookie-analytics" className="font-medium text-sm text-content-primary">
                        Analytics Cookies <span className="text-content-secondary">(Optional)</span>
                      </label>
                      <p id="cookie-analytics-description" className="text-xs text-content-secondary mt-1">
                        These cookies help us understand how you use the platform so we can improve it. We do not use
                        these for marketing purposes.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-content-secondary mt-3">
                For more information, please read our{' '}
                <a href="https://preview.sellercockpit.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 underline">
                  Privacy Policy
                </a>
                . You can change your preferences at any time in Settings.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              {showDetails ? (
                <>
                  <Button onClick={acceptCustom} variant="primary" size="sm">
                    Save Preferences
                  </Button>
                  <Button onClick={acceptAll} variant="outline" size="sm">
                    Accept All
                  </Button>
                  <Button onClick={acceptEssential} variant="outline" size="sm">
                    Essential Only
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={acceptAll} variant="primary" size="sm">
                    Accept All
                  </Button>
                  <Button onClick={acceptEssential} variant="outline" size="sm">
                    Essential Only
                  </Button>
                </>
              )}
            </div>
          </div>

          <button
            onClick={acceptEssential}
            className="flex-shrink-0 p-2 text-content-tertiary hover:text-content-secondary transition-colors"
            aria-label="Dismiss (essential cookies only)"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
